import React from 'react';
import { User, ChevronRight, Mail, Trash2, Send, CheckCircle2, Clock, AlertTriangle, Info } from 'lucide-react';
import { Guest, guestAllergies, guestAltRequested, guestMedicalNote, guestResponseState, guestRestrictions, guestSkippedCategories } from './guestFilters';

const fmtDateTime = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; }
};

const STATUS_TONES: Record<ReturnType<typeof guestResponseState>, { label: string; className: string }> = {
    repondu: { label: 'Répondu', className: 'bg-brand-500/10 text-brand-600 dark:text-brand-400' },
    en_attente: { label: 'En attente', className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
    non_invite: { label: 'Non invité', className: 'bg-surface-soft text-content-muted' },
};

/** Liste de pastilles tronquée — au-delà de 3 entrées la carte perdrait sa hauteur régulière dans la
 *  grille, le détail complet reste accessible dans la fiche détaillée au clic. */
function TagList({ items, className }: { items: string[]; className: string }) {
    const shown = items.slice(0, 3);
    const rest = items.length - shown.length;
    return (
        <div className="flex flex-wrap gap-1">
            {shown.map((item) => (
                <span key={item} className={`text-[10px] font-bold rounded-full px-2 py-0.5 border ${className}`}>{item}</span>
            ))}
            {rest > 0 && <span className="text-[10px] font-bold text-content-muted px-1 py-0.5">+{rest}</span>}
        </div>
    );
}

/** Fiche d'un convive — reprend la carte contact du CRM (CRMModule.tsx, grille de contacts) : mêmes
 *  proportions, même arrondi, même pastille d'avatar, même chevron et même séparateur de pied de carte.
 *  Remplace la ligne de tableau, jugée illisible par Emeni (2026-08-24) sur un événement de 350 convives.
 *  ⚠️ La carte CRM est un <button> ; ici le pied de carte contient ses propres boutons d'action, or un
 *  bouton dans un bouton est du HTML invalide (et casse le clavier). D'où le div role="button" + l'arrêt
 *  de la propagation sur le pied de carte, même principe que les lignes de StatusTrackingTable. */
export function ConviveCard({ guest, sending, onOpen, onSendInvitation, onShowHistory, onDelete }: {
    guest: Guest;
    sending: boolean;
    onOpen: () => void;
    onSendInvitation: () => void;
    onShowHistory: () => void;
    onDelete: () => void;
}) {
    const state = guestResponseState(guest);
    const tone = STATUS_TONES[state];
    const allergies = guestAllergies(guest);
    const restrictions = guestRestrictions(guest);
    const skipped = guestSkippedCategories(guest);
    const altRequested = guestAltRequested(guest);
    const medicalNote = guestMedicalNote(guest);
    const fullName = `${(guest.last_name || '').toUpperCase()} ${guest.first_name || ''}`.trim() || '—';

    return (
        <div role="button" tabIndex={0} onClick={onOpen}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(); } }}
            className="flex flex-col bg-white dark:bg-surface p-5 rounded-2xl border border-border hover:border-gray-300 dark:hover:border-white/20 transition-all text-left group cursor-pointer outline-none focus-visible:border-brand-500">

            <div className="flex items-center gap-3">
                <div className="w-11 h-11 bg-surface-soft rounded-full flex items-center justify-center border border-border text-content-muted group-hover:text-content transition-all shrink-0">
                    <User size={20} />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-slate-900 dark:text-content truncate">{fullName}</h3>
                    <p className="text-xs text-slate-500 dark:text-content-muted truncate">{guest.email}</p>
                </div>
                <ChevronRight className="text-slate-400 dark:text-gray-700 group-hover:text-content transition-all shrink-0" size={18} />
            </div>

            <div className="mt-4 flex items-center gap-2 flex-wrap">
                <span className={`inline-flex text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest whitespace-nowrap ${tone.className}`}>{tone.label}</span>
                {guest.table_number && (
                    <span className="inline-flex text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest bg-surface-soft text-content-muted">Table {guest.table_number}</span>
                )}
                {medicalNote && (
                    <span title={medicalNote} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded uppercase tracking-widest bg-amber-500/10 text-amber-600 dark:text-amber-400">
                        <Info size={10} /> Note médicale
                    </span>
                )}
            </div>

            <div className="mt-3 space-y-1.5 text-xs text-slate-600 dark:text-content-muted">
                <p className="flex items-center gap-2">
                    <Send size={12} className="shrink-0 text-content-muted" />
                    {guest.invitation_sent_at ? `Invité le ${fmtDateTime(guest.invitation_sent_at)}` : 'Invitation pas encore envoyée'}
                </p>
                <p className="flex items-center gap-2">
                    {guest.responded_at
                        ? <><CheckCircle2 size={12} className="shrink-0 text-brand-600 dark:text-brand-400" /> {`Répondu le ${fmtDateTime(guest.responded_at)}`}</>
                        : <><Clock size={12} className="shrink-0 text-content-muted" /> N'a pas encore répondu</>}
                </p>
            </div>

            <div className="mt-3 space-y-2">
                {allergies.length === 0 && restrictions.length === 0 ? (
                    <p className="text-[11px] text-content-muted italic">
                        {guest.responded_at ? 'Aucune allergie ni restriction déclarée' : 'Contraintes inconnues (pas de réponse)'}
                    </p>
                ) : (
                    <>
                        {allergies.length > 0 && (
                            <div className="space-y-1">
                                <p className="text-[10px] font-black text-content-muted uppercase tracking-widest">Allergies</p>
                                <TagList items={allergies} className="bg-red-500/10 border-red-500/20 text-red-500" />
                            </div>
                        )}
                        {restrictions.length > 0 && (
                            <div className="space-y-1">
                                <p className="text-[10px] font-black text-content-muted uppercase tracking-widest">Restrictions</p>
                                <TagList items={restrictions} className="bg-brand-500/10 border-brand-500/20 text-brand-600 dark:text-brand-400" />
                            </div>
                        )}
                    </>
                )}

                {/* Plus voyant que le renoncement juste en dessous, et volontairement : ce convive attend une
                    réponse, il ne s'est pas contenté de décliner. Demande d'Emeni (2026-08-24).
                    Cliquable : le problème était signalé ici, mais l'action se trouvait derrière le petit
                    chevron du haut de la fiche — Emeni (2026-08-24) : « pour l'alternative je ne peux pas ».
                    stopPropagation inutile, la fiche entière mène déjà au même endroit ; le bouton n'est là
                    que pour rendre le geste évident. */}
                {altRequested.length > 0 && (
                    <button type="button" onClick={onOpen}
                        title="Ouvrir la fiche pour proposer un plat"
                        className="w-full text-left flex items-start gap-1.5 text-[11px] font-semibold bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 rounded-lg px-2 py-1.5 hover:bg-red-500/20 hover:border-red-500/50 transition-colors">
                        <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                        <span className="flex-1">
                            Demande une alternative pour {altRequested.join(', ').toLowerCase()}
                            <span className="block text-[10px] font-bold uppercase tracking-wide opacity-80">Proposer un plat</span>
                        </span>
                        <ChevronRight size={12} className="shrink-0 mt-0.5" />
                    </button>
                )}

                {skipped.length > 0 && (
                    <p className="flex items-start gap-1.5 text-[11px] font-semibold bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400 rounded-lg px-2 py-1.5">
                        <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                        <span>N'a pas choisi de {skipped.join(', ')}</span>
                    </p>
                )}
            </div>

            <div className="mt-auto pt-4" onClick={(e) => e.stopPropagation()}>
                <div className="border-t border-border pt-3 flex items-center gap-3 flex-wrap">
                {state !== 'repondu' && (
                    <button type="button" onClick={onSendInvitation} disabled={sending}
                        className="text-[10px] font-bold uppercase tracking-wide text-content-muted hover:text-content disabled:opacity-40">
                        {state === 'en_attente' ? 'Relancer' : "Envoyer l'invitation"}
                    </button>
                )}
                {!!guest.email_count && (
                    <button type="button" onClick={onShowHistory} title="Historique des mails"
                        className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-content-muted hover:text-content">
                        <Mail size={12} /> {guest.email_count}
                    </button>
                )}
                <button type="button" onClick={onDelete} title="Retirer ce convive"
                    className="ml-auto text-content-muted hover:text-red-400"><Trash2 size={13} /></button>
                </div>
            </div>
        </div>
    );
}
